/**
 * Compact family card for the side panel (spec §6.1 "the selected family's
 * card"): identity, position, PnL, the verdict and the latest actions, with
 * a link to the full page. Fetches its own data so any slice can mount it.
 */
import { BreakEvenStat } from "@/components/BreakEven";
import { Link } from "react-router";
import { isApiError } from "@/api/client";
import { useJobs } from "@/api/hooks";
import { AddressLink, WhoBadges } from "@/components/AddressLink";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { KindBadge } from "@/components/KindBadge";
import { Amount, Pct, Price, Usd } from "@/components/Number";
import { Stat } from "@/components/Stat";
import { TimeStamp } from "@/components/TimeStamp";
import { fmtAgo, fmtInt, fmtUsd } from "@/lib/format";
import { routes } from "@/lib/jobs";
import { tokenJobId } from "@/api/types";
import { useFamilyCard } from "./hooks";
import { TransferBasisToggle } from "@/views/holders_pnl/TransferBasisToggle";

export function FamilyCardCompact({
  chain,
  token,
  address,
}: {
  chain: string;
  token: string;
  address: string;
}) {
  const q = useFamilyCard({ chain, token }, address);
  const jobs = useJobs();
  const symbol = jobs.data?.data.tokens.find((j) => j.id === tokenJobId(chain, token))?.symbol ?? "tokens";
  if (q.isLoading) return <EmptyState title="loading family…" />;
  if (q.isError) {
    if (isApiError(q.error) && q.error.isNotFound)
      return (
        <EmptyState
          title="Not a family of this job"
          hint={
            <>
              <span className="num">{address}</span> has no actions, balance or link in {symbol}.
            </>
          }
        />
      );
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  }
  const card = q.data?.data;
  if (!card) return null;
  const who = card.who;
  const pnl = card.pnl;
  const pos = card.position;
  const estimate = (label: string) => `${label}${card.inherit_transfers ? " (est.)" : ""}`;
  const familyHref = `${routes.family(chain, token, card.family)}${card.inherit_transfers ? "?inherit_transfers=1" : ""}`;
  return (
    <div className="flex flex-col gap-2 text-[11px]">
      <div>
        <div className="flex items-center gap-1">
          <Link to={familyHref} className="truncate text-sm font-semibold text-fg hover:text-accent">
            {who.name}
          </Link>
          <WhoBadges who={who} />
        </div>
        <AddressLink
          address={card.family}
          who={who}
          name={card.family}
          link="none"
          showBadges={false}
          className="num text-fg-muted"
        />
        {who.tags.length > 0 && (
          <div className="mt-0.5 text-fg-muted">{who.tags.map((t) => `#${t}`).join(" ")}</div>
        )}
        {card.members.length > 1 && (
          <div className="mt-0.5 text-fg-faint">
            {card.members.length} members:{" "}
            {card.members
              .slice(0, 4)
              .map((m) => m.who.name)
              .join(", ")}
            {card.members.length > 4 ? "…" : ""}
          </div>
        )}
      </div>
      <div>
        <TransferBasisToggle closePanel={false} />
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
        <Stat
          label="balance"
          value={
            pos.balance !== undefined ? (
              <Amount value={pos.balance} symbol={symbol} />
            ) : (
              <Amount value={pos.net} symbol={symbol} />
            )
          }
          sub={
            pos.rank !== undefined
              ? `holder #${fmtInt(pos.rank)}${pos.of !== undefined ? ` of ${fmtInt(pos.of)}` : ""}`
              : "net of actions"
          }
        />
        <Stat
          label={estimate("entry")}
          value={<Price value={card.ledger.entry_price ?? card.basis.entry_price} />}
          sub={`mark ${card.mark ? fmtUsd(card.mark.price_usd) : "–"}`}
        />
        <BreakEvenStat
          value={card.ledger.effective_break_even}
          coverage={card.basis.coverage}
          estimated={card.inherit_transfers}
        />
        <Stat
          label={`pnl${card.inherit_transfers || card.ledger.lp.estimated ? " (est.)" : ""}`}
          value={<Usd value={pnl.total} signed />}
          sub={<Pct value={pnl.pct} />}
          tone={pnl.total > 0 ? "ok" : pnl.total < 0 ? "danger" : "default"}
        />
        <Stat
          label={estimate("realized / unreal.")}
          value={
            <>
              <Usd value={pnl.realized} signed /> / <Usd value={pnl.unrealized} signed />
            </>
          }
        />
        <Stat
          label="activity"
          value={`${card.counts.actions} actions`}
          sub={`${card.counts.buys} buys · ${card.counts.sells} sells · ${card.counts.lp_moves} lp`}
        />
        <Stat label="last seen" value={<TimeStamp t={card.last_at} short />} sub={fmtAgo(card.last_at)} />
      </div>
      {card.verdict && (
        <div
          className={`rounded-sm border px-1.5 py-1 ${card.verdict.accumulating ? "border-ok" : "border-border"}`}
        >
          <span className="font-semibold">
            {card.verdict.accumulating ? "🧲 accumulating" : "not accumulating"}
          </span>
          {card.verdict.failed.length > 0 && (
            <span className="text-fg-muted"> · failed {card.verdict.failed.join(", ")}</span>
          )}
          <div className="text-fg-faint">
            dry powder {card.verdict.dry_powder || "–"} · evaluated {fmtAgo(card.verdict.evaluated_at)}
          </div>
        </div>
      )}
      {card.recent && card.recent.length > 0 && (
        <div>
          <div className="mb-0.5 text-2xs uppercase tracking-wide text-fg-faint">latest actions</div>
          <ul className="space-y-0.5">
            {card.recent.slice(0, 6).map((a) => (
              <li
                key={`${a.tx}:${a.address}:${a.tx_ordinal}`}
                className="flex items-center gap-1"
                title={a.text}
              >
                <TimeStamp t={a.timestamp} short className="w-20 shrink-0 text-fg-muted" />
                <KindBadge kind={a.kind} staking={a.staking} tier={a.tier} flagged={a.flagged} />
                <Amount value={a.amount} className="ml-auto" />
                <Usd value={a.usd} className="w-14 text-right text-fg-muted" />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex gap-1">
        <Link to={familyHref} className="ctl leading-[22px]">
          open family page
        </Link>
        <Link to={`${routes.tape(chain, token)}?family=${card.family}`} className="ctl leading-[22px]">
          tape ↗
        </Link>
      </div>
    </div>
  );
}
